import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import PageSizeDropDownSelection from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import { logout } from "../../app/auth/authSlice.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import ZonePolygonMap, { normalizePoints } from "./zonePolygonMap.jsx";
import "../compnay/ManageCompany.css";

const defaultForm = {
  zone_ID: 0,
  countryCode: "PAK",
  serviceCity: "",
  zoneName: "",
  isActive: true,
  notes: "",
  polygon: [],
};

const ManageMarketplaceZones = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [filterCountry, setFilterCountry] = useState("PAK");
  const [filterCity, setFilterCity] = useState("");
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);

  const fieldStyle = useMemo(
    () => ({
      borderRadius: "10px",
      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
      background: isDarkMode ? "#2d3436" : "#F9F9F9",
      color: isDarkMode ? "#ffffff" : "#495057",
    }),
    [isDarkMode]
  );

  const mapPanelStyle = useMemo(
    () => ({
      borderRadius: "12px",
      border: isDarkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #eceff1",
      background: isDarkMode ? "rgba(45, 52, 54, 0.45)" : "#fafbfc",
      padding: "12px",
    }),
    [isDarkMode]
  );

  const fetchZones = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        CountryCode: filterCountry || "PAK",
        PageNumber: pageNumber,
        PageSize: pageSize.value,
      };
      if (filterCity.trim()) params.ServiceCity = filterCity.trim();

      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_ZONES, { params });
      if (response.data.status) {
        setZones(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        showErrorToast(response.data.message || "Failed to fetch zones");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate("/user/login", { replace: true }), 1000);
      } else {
        showErrorToast("Failed to fetch zones");
      }
    } finally {
      setLoading(false);
    }
  }, [filterCountry, filterCity, pageNumber, pageSize.value, dispatch, navigate]);

  useEffect(() => {
    const handler = setTimeout(() => fetchZones(), 300);
    return () => clearTimeout(handler);
  }, [fetchZones]);

  useEffect(() => {
    setPageNumber(1);
  }, [filterCountry, filterCity, pageSize.value]);

  const openCreate = () => {
    setForm({
      ...defaultForm,
      countryCode: filterCountry || "PAK",
      serviceCity: filterCity || "",
      polygon: [],
    });
    setShowModal(true);
  };

  const openEdit = (zone) => {
    setForm({
      zone_ID: zone.zoneId || zone.Zone_ID || 0,
      countryCode: zone.countryCode || zone.CountryCode || "PAK",
      serviceCity: zone.serviceCity || zone.ServiceCity || "",
      zoneName: zone.zoneName || zone.ZoneName || "",
      isActive: zone.isActive ?? zone.IsActive ?? true,
      notes: zone.notes || zone.Notes || "",
      polygon: normalizePoints(zone.polygon || []),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.countryCode.trim() || !form.serviceCity.trim() || !form.zoneName.trim()) {
      showErrorToast("Country, city, and zone name are required");
      return;
    }
    const polygon = normalizePoints(form.polygon);
    if (polygon.length > 0 && polygon.length < 3) {
      showErrorToast("Polygon needs at least 3 points, or clear it before saving.");
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_UPSERT_MARKETPLACE_ZONE, {
        zone_ID: form.zone_ID,
        countryCode: form.countryCode.trim().toUpperCase(),
        serviceCity: form.serviceCity.trim(),
        zoneName: form.zoneName.trim(),
        isActive: form.isActive,
        notes: form.notes.trim() || null,
        polygon: polygon.length >= 3 ? polygon : null,
      });
      if (response.data.status) {
        showSuccessToast(form.zone_ID > 0 ? "Zone updated" : "Zone created");
        setShowModal(false);
        fetchZones();
      } else {
        showErrorToast(response.data.message || "Failed to save zone");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save zone");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (zone) => {
    const zoneId = zone.zoneId || zone.Zone_ID;
    const nextActive = !(zone.isActive ?? zone.IsActive);
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_SET_MARKETPLACE_ZONE_ACTIVE, {
        zone_ID: zoneId,
        isActive: nextActive,
      });
      if (response.data.status) {
        showSuccessToast(nextActive ? "Zone activated" : "Zone deactivated");
        fetchZones();
      } else {
        showErrorToast(response.data.message || "Failed to update zone");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update zone");
    } finally {
      setLoading(false);
    }
  };

  const actionBtnStyle = (color) => ({
    width: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "6px",
    border: `1px solid ${color}`,
    color,
    padding: 0,
  });

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item active">Service Zones</li>
          </ol>
          <h1 className="page-header mb-0">Go Fatafat Service Zones</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={openCreate}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Zone
          </button>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="mb-4">
            <div
              className="card filter-card"
              style={{
                position: "relative",
                zIndex: 1,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                borderRadius: "12px",
                border: "none",
                boxShadow: `0 4px 20px ${themeColor}40`,
              }}
            >
              <div className="card-body p-3">
                <div className="row g-3 align-items-end">
                  <CountryCitySelector
                    selectedCountry={filterCountry}
                    selectedCity={filterCity}
                    setSelectedCity={setFilterCity}
                    onChange={(payload) => {
                      if (payload.CompCountry !== undefined) setFilterCountry(payload.CompCountry || "PAK");
                      if (payload.CompCity !== undefined) setFilterCity(payload.CompCity || "");
                    }}
                    setLoading={setLoading}
                    selectedValueIsLabel={true}
                    isDarkMode={isDarkMode}
                  />
                  <div className="col-md-2">
                    <PageSizeDropDownSelection
                      defaultValue={pageSize}
                      onChange={setPageSize}
                      styles={dropdownStyles}
                    />
                  </div>
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="btn w-100"
                      onClick={() => {
                        setFilterCountry("PAK");
                        setFilterCity("");
                      }}
                      style={{
                        height: "36px",
                        backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.8)" : "rgba(255, 255, 255, 0.2)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        color: "white",
                        borderRadius: "6px",
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="table-responsive table-container"
            style={{ minHeight: "200px", maxHeight: "450px", overflowY: "auto", overflowX: "auto", position: "relative", zIndex: 1 }}
          >
            <table className="table table-hover align-middle mb-0">
              <thead
                className="sticky-header"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              >
                <tr>
                  <th className="pt-2 pb-2">Country</th>
                  <th className="pt-2 pb-2">City</th>
                  <th className="pt-2 pb-2">Zone</th>
                  <th className="pt-2 pb-2 text-center">Map</th>
                  <th className="pt-2 pb-2">Notes</th>
                  <th className="pt-2 pb-2 text-center">Active</th>
                  <th className="pt-2 pb-2 text-center" style={{ width: 120 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {zones.length > 0 ? (
                  zones.map((zone) => {
                    const id = zone.zoneId || zone.Zone_ID;
                    const isActive = zone.isActive ?? zone.IsActive;
                    const pointCount = zone.polygonPointCount
                      ?? (Array.isArray(zone.polygon) ? zone.polygon.length : 0);
                    return (
                      <tr key={id}>
                        <td>{zone.countryCode || zone.CountryCode}</td>
                        <td>{zone.serviceCity || zone.ServiceCity}</td>
                        <td className="fw-semibold">{zone.zoneName || zone.ZoneName}</td>
                        <td className="text-center">
                          {pointCount >= 3 ? (
                            <span className="badge bg-info text-dark">Mapped ({pointCount})</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td style={{ maxWidth: 220, whiteSpace: "normal" }}>
                          {zone.notes || zone.Notes || "—"}
                        </td>
                        <td className="text-center">
                          <span className={`badge ${isActive ? "bg-success" : "bg-secondary"}`}>
                            {isActive ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-inline-flex justify-content-center gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openEdit(zone)}
                              title="Edit"
                              style={actionBtnStyle(themeColor)}
                            >
                              <i className="fa fa-edit" style={{ fontSize: "12px" }} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => handleToggleActive(zone)}
                              title={isActive ? "Deactivate" : "Activate"}
                              style={actionBtnStyle(isActive ? "#ffc107" : "#28a745")}
                            >
                              <i className={`fa ${isActive ? "fa-ban" : "fa-check"}`} style={{ fontSize: "12px" }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      {filterCity ? "No zones for this city" : "Select a city or create a zone"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">{totalRecords} zone(s)</span>
            <Pagination
              totalRecords={totalRecords}
              pageSize={parseInt(pageSize.value, 10) || 50}
              pageNumber={pageNumber}
              totalPages={totalPages}
              setPageNumber={setPageNumber}
              themeColor={themeColor}
            />
          </div>
        </div>
      </div>

      <CustomModal
        title={form.zone_ID > 0 ? "Edit Zone" : "Add Zone"}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSave}
        showButton={true}
        submitButtonText={form.zone_ID > 0 ? "Update zone" : "Create zone"}
        isCentered
        modalSize="modal-xl"
        maxHeight="90vh"
        minHeight="420px"
        zIndex={10050}
      >
        <div className="row g-3">
          <CountryCitySelector
            selectedCountry={form.countryCode}
            selectedCity={form.serviceCity}
            setSelectedCity={(city) => setForm((prev) => ({ ...prev, serviceCity: city }))}
            onChange={(payload) => {
              setForm((prev) => ({
                ...prev,
                countryCode: payload.CompCountry !== undefined ? (payload.CompCountry || "PAK") : prev.countryCode,
                serviceCity: payload.CompCity !== undefined ? (payload.CompCity || "") : prev.serviceCity,
              }));
            }}
            setLoading={setLoading}
            selectedValueIsLabel={true}
            isDarkMode={isDarkMode}
          />

          <div className="col-md-6">
            <FormInput
              label="Zone name"
              type="text"
              value={form.zoneName}
              onchange={(e) => setForm((prev) => ({ ...prev, zoneName: e.target.value }))}
              placeHolder="e.g. North, Default"
            />
          </div>

          <div className="col-md-6 d-flex align-items-end pb-1">
            <ToggleSwitch
              isChecked={form.isActive}
              onToggle={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              label="Active"
              name="isActive"
            />
          </div>

          <div className="col-12">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Optional notes for this zone"
              style={fieldStyle}
            />
          </div>

          <div className="col-12">
            <div className="d-flex align-items-center gap-2 mb-2">
              <span
                style={{
                  width: 4,
                  height: 16,
                  borderRadius: 4,
                  background: themeColor,
                  flexShrink: 0,
                }}
              />
              <label className="form-label mb-0 fw-semibold">Zone boundary (polygon)</label>
            </div>
            <p className="small text-muted mb-2">
              Click the map to add vertices. Use at least 3 points to save a boundary, or clear to leave unmapped.
            </p>
            <div style={mapPanelStyle}>
              <ZonePolygonMap
                value={form.polygon}
                onChange={(polygon) => setForm((prev) => ({ ...prev, polygon }))}
                serviceCity={form.serviceCity}
                countryCode={form.countryCode}
              />
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default ManageMarketplaceZones;
